import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import type { Payment } from "@/types";
import api from "@/services/api";

export interface PaymentFilter {
  status?: Payment["status"];
  method?: string;
  debtorId?: string;
  searchTerm?: string;
}

export interface PaymentSort {
  field: keyof Payment | null;
  direction: "asc" | "desc";
}

interface PaymentsState {
  items: Payment[];
  loading: boolean;
  error: string | null;
  filter: PaymentFilter;
  sort: PaymentSort;
  currentPage: number;
  pageSize: number;
  total: number;
}

const initialState: PaymentsState = {
  items: [],
  loading: false,
  error: null,
  filter: {},
  sort: { field: null, direction: "asc" },
  currentPage: 1,
  pageSize: 10,
  total: 0,
};

export const fetchPayments = createAsyncThunk(
  "payments/fetchPayments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<any>("/v1/payments");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch payments");
    }
  }
);

const paymentsSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    setPaymentFilter: (state, action: PayloadAction<PaymentFilter>) => {
      state.filter = action.payload;
      state.currentPage = 1;
    },
    setPaymentSort: (state, action: PayloadAction<PaymentSort>) => {
      state.sort = action.payload;
      state.currentPage = 1;
    },
    setPaymentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPaymentPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    resetPaymentFilters: (state) => {
      state.filter = {};
      state.sort = { field: null, direction: "asc" };
      state.currentPage = 1;
    },
    addPayment: (state, action: PayloadAction<Payment>) => {
      state.items.unshift(action.payload);
      state.total += 1;
    },
    updatePayment: (state, action: PayloadAction<Payment>) => {
      const index = state.items.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    deletePayment: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((p) => p.id !== action.payload);
      state.total -= 1;
    },
    resetPayments: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.items = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
        state.total = state.items.length;
        state.error = null;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPaymentFilter,
  setPaymentSort,
  setPaymentPage,
  setPaymentPageSize,
  resetPaymentFilters,
  addPayment,
  updatePayment,
  deletePayment,
  resetPayments,
} = paymentsSlice.actions;
export default paymentsSlice.reducer;

// Selectors
export const selectPayments = (state: any) => state.payments.items;
export const selectPaymentsLoading = (state: any) => state.payments.loading;
export const selectPaymentsError = (state: any) => state.payments.error;
export const selectPaymentFilter = (state: any) => state.payments.filter;
export const selectPaymentSort = (state: any) => state.payments.sort;
export const selectPaymentPagination = (state: any) => ({
  currentPage: state.payments.currentPage,
  pageSize: state.payments.pageSize,
  total: state.payments.total,
});

export const selectFilteredPayments = createSelector(
  [(state: any) => state.payments.items, (state: any) => state.payments.filter, (state: any) => state.payments.sort, (state: any) => state.payments.currentPage, (state: any) => state.payments.pageSize],
  (items, filter, sort, currentPage, pageSize) => {
    let filtered = [...items];

    if (filter.status) {
      filtered = filtered.filter((p) => p.status === filter.status);
    }
    if (filter.debtorId) {
      filtered = filtered.filter((p) => p.debtorId === filter.debtorId);
    }
    if (filter.method) {
      filtered = filtered.filter((p) => p.method === filter.method);
    }
    if (filter.searchTerm) {
      const term = filter.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.id.toLowerCase().includes(term) ||
          p.debtorName?.toLowerCase().includes(term)
      );
    }

    if (sort.field) {
      filtered.sort((a, b) => {
        const aVal = a[sort.field!] || "";
        const bVal = b[sort.field!] || "";
        const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        return sort.direction === "asc" ? comparison : -comparison;
      });
    }

    const startIdx = (currentPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginated = filtered.slice(startIdx, endIdx);

    return {
      data: paginated,
      total: filtered.length,
      currentPage,
      pageSize,
      hasNextPage: endIdx < filtered.length,
      hasPrevPage: currentPage > 1,
    };
  }
);
